import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
    Aangever,
    AangeverBurgerlijkeStaat,
    AangeverSamenwoonSituatie,
    Aangifte, AangifteRapport, BokkzAlgDataService, BokkzClient,
    BokkzIbDataService,
    IncomeTaxEntity, Periode, Woonland
} from '@snelstart-ventures/fiscaal/domain-logic';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { Subject, takeUntil } from 'rxjs';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { IackInfoComponent } from '../../aanvullende-vragen/iack-info/iack-info.component';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { CommonModule } from '@angular/common';
import {
    optionsLandcodesMetNLD,
    optionsLandcodesZonderNLD
} from '../../gedeelde-componenten/gedeelde-componenten.component';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-partner',
    templateUrl: './partner.component.html',
    styleUrls: ['./partner.component.scss'],
    imports: [
        SharedUiDetailsModule,
        SharedUiFormControlsModule,
        IackInfoComponent,
        SharedUiAlertModule,
        CommonModule
    ]
})
export class PartnerComponent implements OnInit, OnDestroy {

    @Output() aangifteChange = new EventEmitter<Aangifte>();
    protected readonly AangeverBurgerlijkeStaat = AangeverBurgerlijkeStaat;
    protected readonly AangeverSamenwoonSituatie = AangeverSamenwoonSituatie;
    protected readonly optionsLandcodesMetNld = optionsLandcodesMetNLD;
    protected readonly optionsLandcodesZonderNld = optionsLandcodesZonderNLD;
    private destroy$ = new Subject<void>();
    public clientId!: string;
    public aangifteRapport = {} as AangifteRapport;
    public incomeTaxEntity = {} as IncomeTaxEntity;
    public partnerStaatInSnelStartRelatielijst = true;
    public partnerPersoonlijkFg = {} as FormGroup;
    public gezamenlijkeAangifteFg = {} as FormGroup;
    public woonlandFg = {} as FormGroup;

    public optionsJaNee: SelectFormControlItem<boolean | undefined>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' }
    ];

    constructor(
        public fb: FormBuilder,
        private cdr: ChangeDetectorRef,
        public bokkzClient: BokkzClient,
        public dataService: BokkzIbDataService,
        private dataAlgService: BokkzAlgDataService) {
        this.partnerPersoonlijkFg = this.fb.group<Aangever>({
            voorletters: null as unknown as string,
            tussenvoegsels: null as unknown as string,
            achternaam: null as unknown as string,
            geboortedatum: null as unknown as Date,
            overlijdensdatum: null as unknown as Date,
            bsn: null as unknown as string,
            burgerlijkeStaat: '' as unknown as AangeverBurgerlijkeStaat,
            samenwoonSituatie: '' as unknown as AangeverSamenwoonSituatie,
            landCodeNationaliteit: null as unknown as string
        });
        this.gezamenlijkeAangifteFg = this.fb.group({
            gezamenlijkeAangifte: null as unknown as boolean
        });
        this.woonlandFg = this.fb.group({
            inwonerLandNonDiscriminatie: false,
            woonlandVoldoetVoorKwalificatie: false,
            belastingplichtigeVoldoetAanVoorwaardenKwalificatie: false,
            landCodeWoonland: null as unknown as string,
            periodeWoonachtigInWoonland: this.fb.group<Periode>({
                begindatum: null as unknown as Date,
                einddatum: null as unknown as Date
            })
        });
    }

    ngOnInit(): void {
        this.dataService.incomeTaxEntity.subscribe((incomeTaxEntity) => {
            this.incomeTaxEntity = incomeTaxEntity;
            if (incomeTaxEntity.aangifte) {
                if (incomeTaxEntity.aangifte?.partnerAangifte && incomeTaxEntity.aangifte.partnerAangifte?.aangever) {
                    this.partnerPersoonlijkFg.patchValue(incomeTaxEntity.aangifte?.partnerAangifte.aangever, { emitEvent: false });
                    if (incomeTaxEntity.aangifte.partnerAangifte.aangever.bsn) {
                        this.getPartnerEntity(incomeTaxEntity.aangifte?.partnerAangifte?.aangever?.bsn!, String(incomeTaxEntity.aangifte.aangifteJaar));
                    }
                }
                if (incomeTaxEntity.aangifte?.gezamenlijkeAangifte !== undefined) {
                    this.gezamenlijkeAangifteFg.patchValue({ gezamenlijkeAangifte: incomeTaxEntity.aangifte?.gezamenlijkeAangifte }, { emitEvent: false });
                }
                if (incomeTaxEntity.aangifte.partnerAangifte?.buitenlandseBelastingplicht?.woonlandGegevens?.at(0)) {
                    this.patchWoonlandGegevens(String(incomeTaxEntity.year));
                }
            }
        });
        this.dataService.aangifteRapport.subscribe((value) => {
            if (value.isBeSuNaAr) {
                this.aangifteRapport = value;
            }
        });
        this.dataAlgService.client.pipe(takeUntil(this.destroy$)).subscribe((client) => {
            this.clientId = client.sstRelationId!;
        });
        this.cdr.detectChanges();
    }

    ngOnDestroy(): void {
        if (this.incomeTaxEntity?.sstRelationId) {
            this.dataService.postIncomeTaxEntity(
                this.incomeTaxEntity.sstRelationId,
                false,
                this.incomeTaxEntity);
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    public patchWoonlandGegevens(year: string, save = false) {
        const buitenlandseBelastingplicht = this.incomeTaxEntity.aangifte?.partnerAangifte?.buitenlandseBelastingplicht;
        const woonland = this.incomeTaxEntity.aangifte?.partnerAangifte?.buitenlandseBelastingplicht?.woonlandGegevens[0] as Woonland;
        const woonland1 = {
            landCodeWoonland: woonland.landCodeWoonland ? woonland.landCodeWoonland : null,
            periodeWoonachtigInWoonland: {
                begindatum: new Date(`${year}-01-01`),
                einddatum: new Date(`${year}-12-31`)
            }
        } as Woonland;
        this.woonlandFg.patchValue(woonland1);

        this.woonlandFg.patchValue({ inwonerLandNonDiscriminatie: false });
        if (['BEL', 'SUR', 'AWB'].includes(woonland1.landCodeWoonland)) {
            this.woonlandFg.patchValue({ inwonerLandNonDiscriminatie: true });
        }

        this.woonlandFg.patchValue({ woonlandVoldoetVoorKwalificatie: false });
        if (['ALA', 'ABW', 'BEL', 'BES', 'BGR', 'CYP', 'PRT', 'DNK', 'DEU', 'EST', 'FIN',
            'FRA', 'GUF', 'GRC', 'GLP', 'HUN', 'IRL', 'ITA', 'HRV', 'LVA', 'LTU', 'LIE',
            'LUX', 'MLT', 'MTQ', 'MYT', 'NOR', 'AUT', 'POL', 'PRT', 'REU', 'ROU', 'MAF',
            'SVN', 'SVK', 'ESP', 'SUR', 'CZE', 'SWE', 'CHE'].includes(woonland1.landCodeWoonland)) {
            this.woonlandFg.patchValue({ woonlandVoldoetVoorKwalificatie: true });
        }

        if (buitenlandseBelastingplicht?.belastingplichtigeVoldoetAanVoorwaardenKwalificatie !== undefined) {
            this.woonlandFg.patchValue({
                belastingplichtigeVoldoetAanVoorwaardenKwalificatie: buitenlandseBelastingplicht.belastingplichtigeVoldoetAanVoorwaardenKwalificatie
            });
        }

        if (save) {
            this.saveForm();
        }
    }

    public saveForm(): void {
        setTimeout(() => {
            this.partnerPersoonlijkFg.updateValueAndValidity();
            this.gezamenlijkeAangifteFg.updateValueAndValidity();
            this.woonlandFg.updateValueAndValidity();
            this.incomeTaxEntity.taxPayerPartnerFiscalNumber = this.partnerPersoonlijkFg.getRawValue().bsn;
            this.incomeTaxEntity.aangifte = {
                ...this.incomeTaxEntity.aangifte,
                gezamenlijkeAangifte: this.gezamenlijkeAangifteFg.getRawValue().gezamenlijkeAangifte,
                partnerAangifte: {
                    ...this.incomeTaxEntity.aangifte?.partnerAangifte,
                    biljetSoort: this.incomeTaxEntity.aangifte?.biljetSoort,
                    aangifteJaar: this.incomeTaxEntity.aangifte?.aangifteJaar,
                    aangever: {
                        voorletters: this.partnerPersoonlijkFg.getRawValue().voorletters,
                        tussenvoegsels: this.partnerPersoonlijkFg.getRawValue().tussenvoegsels,
                        achternaam: this.partnerPersoonlijkFg.getRawValue().achternaam,
                        geboortedatum: this.partnerPersoonlijkFg.getRawValue().geboortedatum,
                        bsn: this.partnerPersoonlijkFg.getRawValue().bsn,
                        burgerlijkeStaat: this.incomeTaxEntity.aangifte?.aangever.burgerlijkeStaat,
                        samenwoonSituatie: this.incomeTaxEntity.aangifte?.aangever.samenwoonSituatie,
                        landCodeNationaliteit: this.partnerPersoonlijkFg.getRawValue().landCodeNationaliteit
                    },
                    buitenlandseBelastingplicht: {
                        ...this.incomeTaxEntity.aangifte?.partnerAangifte?.buitenlandseBelastingplicht,
                        inwonerLandNonDiscriminatie: this.woonlandFg.getRawValue().inwonerLandNonDiscriminatie,
                        woonlandVoldoetVoorKwalificatie: this.woonlandFg.getRawValue().woonlandVoldoetVoorKwalificatie,
                        belastingplichtigeVoldoetAanVoorwaardenKwalificatie: this.woonlandFg.getRawValue().belastingplichtigeVoldoetAanVoorwaardenKwalificatie,
                        woonlandGegevens: [this.woonlandFg.getRawValue()]
                    }
                }
            } as Aangifte;
            this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
        }, 0);
    }

    public showGezamenlijkeAangifte(): boolean {
        return !!this.aangifteRapport.speciaficatiePartnerschap?.opbouw?.heeftFiscaalPartner;
    }

    public getPartnerEntity(bsn: string, year: string) {
        this.bokkzClient.getIncomeTax(bsn, year).subscribe(value => {
            this.partnerStaatInSnelStartRelatielijst = value.sstRelationId != undefined;
        });
    }
}
